import { ProjectActions } from '@octopus-do/octopus-project-core'
import { managerActions } from '@store/reducers/manager/actions'
import type { AppState } from '@store/types'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { type CrawledPageBlocks, CrawlImportActions } from './actions'
import { buildCrawledBlock, resolveBlockColorId } from './utils'

type BlocksGroup = Parameters<
  typeof ProjectActions.blocks.addMany
>[0]['groups'][number]

// About this many blocks are parsed between two hand-backs of the thread: a few
// milliseconds of markdown, so the crawler modal keeps animating and counting.
const BLOCKS_PER_CHUNK = 25

// Resolves in the next macrotask, so the browser can paint and handle input in
// between. A message port rather than setTimeout: a hidden tab clamps timers to
// once a second (and once a minute after a while), which would stretch a
// sixty-chunk import over minutes; React's scheduler yields the same way.
const yieldToBrowser = (): Promise<void> =>
  new Promise((resolve) => {
    const channel = new MessageChannel()

    channel.port1.onmessage = () => resolve()
    channel.port2.postMessage(null)
  })

const countBlocks = (pages: CrawledPageBlocks[]): number =>
  pages.reduce((sum, page) => sum + page.blocks.length, 0)

// Pages cut into runs of about BLOCKS_PER_CHUNK blocks; a page is never split.
export const chunkPages = (
  pages: CrawledPageBlocks[]
): CrawledPageBlocks[][] => {
  const { chunks } = pages.reduce(
    ({ chunks, size }, page) => {
      const opensChunk = chunks.length === 0 || size >= BLOCKS_PER_CHUNK

      return opensChunk
        ? { chunks: [...chunks, [page]], size: page.blocks.length }
        : {
            chunks: [
              ...chunks.slice(0, -1),
              [...chunks[chunks.length - 1], page],
            ],
            size: size + page.blocks.length,
          }
    },
    { chunks: [] as CrawledPageBlocks[][], size: 0 }
  )

  return chunks
}

// One group per page keeps the crawler's block order; the pages themselves are
// already in the store.
const buildGroups = (
  pages: CrawledPageBlocks[],
  state: AppState
): BlocksGroup[] =>
  pages
    .filter((page) => page.blocks.length > 0)
    .map((page) => ({
      items: page.blocks.map((block) =>
        buildCrawledBlock({
          block,
          colorId: resolveBlockColorId(state, block.color),
        })
      ),
      index: 0,
      parents: [page.nodeId],
    }))

// Lays the blocks the crawler inferred under their pages (OCT-4990), then saves
// the project. The markdown is parsed chunk by chunk with the thread handed back
// in between, and the blocks land in one action: one add per page made the
// import quadratic on the store size, and the modal froze for seconds (OCT-5101).
export function* finishCrawlImport({
  payload: { pages, save },
}: ReturnType<typeof CrawlImportActions.finish>): Generator<any, void, any> {
  const state: AppState = yield select((currentState) => currentState)
  const total = countBlocks(pages)
  const chunks = total > 0 ? chunkPages(pages) : []
  const groups: BlocksGroup[] = []

  try {
    for (const [index, chunk] of chunks.entries()) {
      groups.push(...buildGroups(chunk, state))

      yield put(
        CrawlImportActions.progress({
          done: countBlocks(chunks.slice(0, index + 1).flat()),
          total,
        })
      )
      yield call(yieldToBrowser)
    }

    if (groups.length > 0) {
      yield put(ProjectActions.blocks.addMany({ groups }))
    }
  } finally {
    yield put(CrawlImportActions.progress(null))
  }

  yield put(managerActions.saveProjectSaga(save))
}

export function* crawlImportSaga() {
  yield all([takeLatest(CrawlImportActions.finish, finishCrawlImport)])
}
